import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Camera, ForkKnife, FilePdf, ArrowRight, X } from "@phosphor-icons/react";

const STORAGE_KEY = "tally:onboarded";

const slides = [
  {
    Icon: Camera,
    title: "Snap your meal",
    body: "Point your camera at a plate. Our AI will spot each ingredient and estimate its carbs.",
  },
  {
    Icon: ForkKnife,
    title: "Edit if needed, then save",
    body: "Tap any number to adjust the portion. Tally remembers every scan in your daily log.",
  },
  {
    Icon: FilePdf,
    title: "Share a clean weekly report",
    body: "Tally generates a polished PDF every week — perfect to forward to your doctor.",
  },
];

export default function Onboarding({ user }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    const key = `${STORAGE_KEY}:${user.user_id}`;
    if (localStorage.getItem(key) !== "1") {
      setOpen(true);
    }
  }, [user]);

  const close = () => {
    if (user) {
      try { localStorage.setItem(`${STORAGE_KEY}:${user.user_id}`, "1"); } catch {}
    }
    setOpen(false);
    setStep(0);
  };

  if (!open) return null;

  const s = slides[step];
  const isLast = step === slides.length - 1;
  const SlideIcon = s.Icon;

  return (
    <div className="fixed inset-0 z-50 bg-[#1A2421]/45 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pb-4" data-testid="onboarding-overlay">
      <div className="w-full max-w-md rounded-3xl bg-[#F9F8F6] p-6 sm:p-8 relative overflow-hidden">
        {/* Skip */}
        <button
          onClick={close}
          className="absolute top-4 right-4 text-[#5C6B62] hover:text-[#1A2421]"
          data-testid="onboarding-skip"
          aria-label="Skip onboarding"
        >
          <X size={16} />
        </button>

        {/* Icon */}
        <div className="flex justify-center pt-2">
          <span className="w-16 h-16 rounded-full bg-[#2B422F] flex items-center justify-center">
            <SlideIcon size={28} weight="regular" className="text-[#E6D4C4]" />
          </span>
        </div>

        {/* Content */}
        <div className="mt-6 text-center">
          <span className="label-eyebrow">{step + 1} of {slides.length}</span>
          <h2 className="font-serif-display text-3xl text-[#1A2421] mt-2 tracking-tight">{s.title}</h2>
          <p className="text-sm text-[#5C6B62] mt-3 leading-relaxed max-w-xs mx-auto">{s.body}</p>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-1.5 mt-6">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? "w-6 bg-[#2B422F]" : "w-1.5 bg-[#E6E3D8]"}`}
            />
          ))}
        </div>

        {/* Action */}
        <button
          onClick={() => (isLast ? close() : setStep(step + 1))}
          className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
          data-testid="onboarding-next"
        >
          {isLast ? "Let's start" : "Next"}
          <ArrowRight size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
